import {
  SpaceDashboard,
  Apartment,
  Send,
  Person,
  QuestionMark,
  Logout,
  AssignmentOutlined,
  Home,
  Shield,
  BarChartOutlined,
  PeopleOutlined,
} from '@mui/icons-material';
import { externalLinks, externalTitles, routes } from '@/constants/Constants';
import { useLocation } from 'react-router';
import { useGoToExternal, useNavigateWithSegment } from '@/utils/linkUtils';
import { Box, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import useProfileDrawerStore from '@/stores/useProfileDrawerStore';
import { useQuery } from '@tanstack/react-query';
import getUser from '@/utils/apiRestCalls/getUser';
import logout from '@/utils/axios/logout';
import { shouldDisableRenterInvite, shouldUserStayInEnrollProperty } from '@/utils/commonUtils';
import { useUrlChecker } from '@/hooks/useUrlChecker';
import { ZgriVisibilityGuard } from '@/features/RentersInsurance/guards';
import { RiNewBadge } from '@/features/RentersInsurance/components';
import { CoreVisibilityGuard } from '@/components/guards';
import { UserPermissions } from '@/constants/permissions.constant';
import { usePermission } from '@/hooks/usePermission';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import SideMenuItem from '@/appWrapper/SideMenuItem/V1';
import { useFeatureFlag } from '@/hooks';

interface Props {
  menuOpen: boolean;
}

const MenuContent = ({ menuOpen }: Props) => {
  const navigate = useNavigateWithSegment();
  const goToExternal = useGoToExternal();
  const location = useLocation();
  const { showProfileDrawer } = useProfileDrawerStore();
  const { isPublic } = useUrlChecker();
  const { track } = useAnalyticsContext();

  const isSSUMEnabled = useFeatureFlag('otters-3986_self_service_user_management_enabled');

  const { enabled: canManageUsersEnabled } = usePermission(UserPermissions.User.CanManageUsers);

  const { notSet: canEnrollPropertiesNotSet, enabled: canEnrollPropertiesEnabled } = usePermission(
    UserPermissions.Properties.CanEnrollProperty,
  );
  const { notSet: userCanFileClaimNotSet, enabled: userCanFileClaimEnabled } = usePermission(
    UserPermissions.Claim.CanFileClaim,
  );
  const { notSet: userCanInviteRenterNotSet, enabled: userCanInviteRenterEnabled } = usePermission(
    UserPermissions.Invite.CanInviteRenter,
  );

  const canEnrollProperties = canEnrollPropertiesNotSet || canEnrollPropertiesEnabled;
  const userCanInviteRenter = userCanInviteRenterNotSet || userCanInviteRenterEnabled;
  const userCanFileClaim = userCanFileClaimNotSet || userCanFileClaimEnabled;

  const { notSet: userCanAccessDealsDashboardNotSet, enabled: userCanAccessDealsDashboardEnabled } =
    usePermission(UserPermissions.Deal.CanAccessDealsDashboard);

  const userCanAccessDealsDashboard =
    userCanAccessDealsDashboardNotSet || userCanAccessDealsDashboardEnabled;

  const { enabled: userCanViewReportEnabled } = usePermission(UserPermissions.Claim.CanViewReport);

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: () => getUser(),
    staleTime: Infinity,
  });

  const onLogOut = () => {
    track('Logout Button is clicked', {
      title: 'Logout',
      referrer: window.location.pathname,
    });
    logout();
  };

  const onProfile = () => {
    track('Profile Button is clicked', {
      title: 'Profile',
      referrer: window.location.pathname,
    });
    showProfileDrawer();
  };

  const goToFAQ = () => {
    if (user && !canEnrollProperties) {
      return goToExternal(externalLinks.operatorsFAQ, {
        title: 'FAQ Menu (Operator)',
        path: externalLinks.operatorsFAQ,
      });
    }

    return goToExternal(externalLinks.faq, {
      title: 'FAQ Menu',
      path: 'enroll-property#faq',
    });
  };

  const shouldDisableActionIcons = shouldUserStayInEnrollProperty(location.pathname, user);
  const userIsOnHome = location.pathname === routes.home;
  const userIsOnPropertyEnrollment = location.pathname.includes(routes.propertyEnrollmentForm);
  const userIsOnRenterInvite = location.pathname.includes(routes.renterInvite);
  const userIsOnDealsDashboard = location.pathname.includes(routes.dealsDashboard);
  const userIsOnClaimsReport = location.pathname.includes(routes.claimsReport);
  const userIsOnClaimsForm =
    location.pathname.includes(routes.claimsForm) ||
    location.pathname.includes(routes.claimsFormInstructions) ||
    location.pathname.includes(routes.claimsThankYou);

  return (
    <Grid
      container
      sx={{
        height: '100%',
        pb: '8px',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        flexDirection: 'column',
      }}
    >
      {!isPublic && (
        <Grid id="firstColumnIcons">
          <SideMenuItem
            open={menuOpen}
            text="Home"
            icon={<Home />}
            disabled={shouldDisableActionIcons && !userIsOnHome}
            selected={userIsOnHome}
            action={() => navigate(routes.home, { title: 'Visit Home Menu', path: '/' })}
          />
          {user && userCanInviteRenter && (
            <CoreVisibilityGuard>
              <SideMenuItem
                open={menuOpen}
                text="Invite Renter"
                icon={<Send />}
                action={() => navigate(routes.renterInvite)}
                selected={userIsOnRenterInvite}
                disabled={
                  (shouldDisableActionIcons && !userIsOnRenterInvite) ||
                  shouldDisableRenterInvite(user)
                }
              />
            </CoreVisibilityGuard>
          )}
          {canEnrollProperties && (
            <SideMenuItem
              open={menuOpen}
              text="Enroll Properties"
              icon={<Apartment />}
              disabled={shouldDisableActionIcons && !userIsOnPropertyEnrollment}
              selected={userIsOnPropertyEnrollment}
              action={() =>
                navigate(routes.propertyEnrollmentForm, {
                  title: 'Visit Enroll Properties Menu',
                  path: '/enroll/property-type',
                })
              }
            />
          )}
          {user && userCanAccessDealsDashboard && (
            <CoreVisibilityGuard>
              <SideMenuItem
                open={menuOpen}
                text={'Application Tracker'}
                icon={<SpaceDashboard />}
                disabled={shouldDisableActionIcons && !userIsOnDealsDashboard}
                selected={userIsOnDealsDashboard}
                action={() => {
                  navigate(routes.dealsDashboard);
                }}
              />
            </CoreVisibilityGuard>
          )}
          <ZgriVisibilityGuard>
            <SideMenuItem
              open={menuOpen}
              text="Renters Insurance"
              displayText={
                <Box component={'span'} display={'flex'} alignItems={'center'} columnGap={'8px'}>
                  <Typography component={'span'}>Renters Insurance</Typography>
                  <RiNewBadge />
                </Box>
              }
              icon={<Shield />}
              action={() =>
                navigate(routes.rentersInsurance, {
                  title: 'Visit Renters Insurance',
                  path: '/renters-insurance',
                })
              }
              selected={location.pathname.includes(routes.rentersInsurance)}
            />
          </ZgriVisibilityGuard>

          {userCanFileClaim && (
            <CoreVisibilityGuard>
              <SideMenuItem
                open={menuOpen}
                text={'File a Claim'}
                disabled={shouldDisableActionIcons || !userCanFileClaim}
                icon={<AssignmentOutlined />}
                action={() => navigate(routes.claimsFormInstructions)}
                selected={userIsOnClaimsForm}
              />
            </CoreVisibilityGuard>
          )}

          {userCanViewReportEnabled && (
            <SideMenuItem
              open={menuOpen}
              text="Track Claims"
              disabled={shouldDisableActionIcons && !userIsOnClaimsReport}
              icon={<BarChartOutlined />}
              action={() => navigate(routes.claimsReport)}
              selected={userIsOnClaimsReport}
            />
          )}
        </Grid>
      )}
      <Grid>
        <Divider sx={{ m: '8px 0' }} />
        {isSSUMEnabled && canManageUsersEnabled && (
          <SideMenuItem
            open={menuOpen}
            text="Users"
            icon={<PeopleOutlined />}
            action={() => navigate(routes.users)}
          />
        )}
        {!isPublic && (
          <SideMenuItem open={menuOpen} text="Profile" icon={<Person />} action={onProfile} />
        )}
        <SideMenuItem
          open={menuOpen}
          text={externalTitles.faq}
          icon={<QuestionMark />}
          action={goToFAQ}
        />
        <SideMenuItem open={menuOpen} text="Logout" icon={<Logout />} action={onLogOut} />
      </Grid>
    </Grid>
  );
};

export default MenuContent;
